import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useModal } from '../context/ModalContext';
import { useContent } from '../context/ContentContext';
import { Target, Lightbulb, Users, Award, CheckCircle } from 'lucide-react';

const ICON_MAP = { target: Target, lightbulb: Lightbulb, users: Users, award: Award };

const AboutSection = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { openModal } = useModal();
  const { siteContent } = useContent();
  const isRtl = language === 'ar';

  const aboutData = siteContent?.about || {};
  const imageUrl = aboutData.image_url || '/uploads/7152c341-a5e6-4b26-bc37-d855eeb33ced.jpg';
  const highlights = language === 'ar'
    ? (aboutData.highlights_ar || ['معايير جودة عالمية', 'تقنيات تصنيع متطورة', 'فريق من الخبراء المحترفين', 'شراكات دولية قوية'])
    : (aboutData.highlights_en || ['World-class quality standards', 'Advanced manufacturing technology', 'Expert professional team', 'Strong international partnerships']);

  const rawValues = aboutData.values || [];
  const values = rawValues.filter(v => v.enabled !== false).sort((a, b) => (a.order || 0) - (b.order || 0));

  const stats = [
    { key: 'employees', value: t('about.stats.employees'), label: t('about.stats.employeesLabel') },
    { key: 'experience', value: t('about.stats.experience'), label: t('about.stats.experienceLabel') },
    { key: 'markets', value: t('about.stats.markets'), label: t('about.stats.marketsLabel') },
  ];

  return (
    <section id="about" data-testid="about-section" className={`py-20 sm:py-28 ${isDark ? 'bg-[#0A0A0A]' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[var(--gold-primary)] uppercase tracking-[0.2em] text-xs font-semibold mb-4" data-testid="about-overline">{t('about.overline')}</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 tracking-tight" data-testid="about-title">
            <span className="gold-text">{t('about.title')}</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start mb-20">
          <div className="relative">
            <div className="relative">
              <div className="aspect-[4/3] overflow-hidden rounded-sm">
                <img src={imageUrl} alt="Royal Tobacco Headquarters" className="w-full h-full object-cover" />
              </div>
              <div className={`absolute -bottom-4 w-32 h-32 border-2 border-[var(--gold-primary)] rounded-sm ${language === 'ar' ? '-left-4' : '-right-4'}`} />
              <div className={`absolute -bottom-8 ${language === 'ar' ? '-left-4 sm:-left-8' : '-right-4 sm:-right-8'} grid grid-cols-3 gap-3 p-4 rounded-sm shadow-xl ${isDark ? 'bg-[#111111]/95 backdrop-blur-sm border border-[var(--border-color)]' : 'bg-white/95 backdrop-blur-sm border border-gray-200'}`}>
                {stats.map(stat => (
                  <div key={stat.key} className="text-center px-3 py-2" data-testid={`stat-${stat.key}`}>
                    <p className="font-display text-2xl sm:text-3xl font-bold gold-text">{stat.value}</p>
                    <p className={`text-[10px] sm:text-xs uppercase tracking-wider ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={`${isRtl ? 'text-right' : 'text-left'} pt-4`}>
            <p className={`text-base sm:text-lg leading-relaxed mb-6 ${isDark ? 'text-white/80' : 'text-gray-600'}`} data-testid="about-description-1">{t('about.description1')}</p>
            <p className={`text-base sm:text-lg leading-relaxed mb-8 ${isDark ? 'text-white/70' : 'text-gray-500'}`} data-testid="about-description-2">{t('about.description2')}</p>
            <div className="space-y-3 mb-8">
              {highlights.map((item, index) => (
                <div key={index} className={`flex items-center gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle className="w-5 h-5 text-[var(--gold-primary)] flex-shrink-0" />
                  <span className={`text-sm sm:text-base ${isDark ? 'text-white/70' : 'text-gray-600'}`}>{item}</span>
                </div>
              ))}
            </div>
            <div className={`inline-flex items-center gap-3 px-5 py-3 rounded-sm ${isRtl ? 'flex-row-reverse text-right' : ''} ${isDark ? 'bg-[var(--gold-primary)]/10 border border-[var(--gold-primary)]/30' : 'bg-[var(--gold-primary)]/5 border border-[var(--gold-primary)]/20'}`}>
              <Award className="w-8 h-8 text-[var(--gold-primary)]" />
              <div>
                <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>{language === 'ar' ? 'الجودة المعتمدة' : 'Quality Certified'}</p>
                <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{language === 'ar' ? 'معايير ISO الدولية' : 'ISO International Standards'}</p>
              </div>
            </div>
          </div>
        </div>

        {values.length > 0 && (
          <div className="mt-24">
            <h3 className={`font-display text-2xl sm:text-3xl font-semibold mb-10 text-center ${isDark ? 'text-white' : 'text-gray-800'}`}>
              {language === 'ar' ? 'قيمنا الأساسية' : 'Our Core Values'}
            </h3>
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {values.map((value, index) => {
                const ValIcon = ICON_MAP[value.icon] || Target;
                const title = language === 'ar' ? value.title_ar : value.title_en;
                const description = language === 'ar' ? value.description_ar : value.description_en;
                return (
                  <div
                    key={value.id}
                    className={`group relative p-8 rounded-sm transition-all duration-300 cursor-pointer ${isRtl ? 'text-right' : 'text-left'} ${isDark ? 'bg-[#111111] border border-[var(--border-color)] hover:border-[var(--gold-primary)]/50' : 'bg-gray-50 border border-gray-200 hover:border-[var(--gold-primary)]/50'}`}
                    data-testid={`value-${value.id}`}
                    onClick={() => openModal('about-value', { key: value.id, title, description })}
                    role="button" tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && openModal('about-value', { key: value.id, title, description })}
                  >
                    <span className={`absolute top-4 ${language === 'ar' ? 'left-4' : 'right-4'} font-display text-5xl font-bold ${isDark ? 'text-white/5' : 'text-gray-200'} group-hover:text-[var(--gold-primary)]/10 transition-colors`}>0{index + 1}</span>
                    <div className={`w-14 h-14 flex items-center justify-center rounded-sm mb-6 transition-colors ${isDark ? 'bg-[var(--gold-primary)]/10 group-hover:bg-[var(--gold-primary)]/20' : 'bg-[var(--gold-primary)]/10 group-hover:bg-[var(--gold-primary)]/20'}`}>
                      <ValIcon className="w-7 h-7 text-[var(--gold-primary)]" />
                    </div>
                    <h4 className={`font-display text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>{title}</h4>
                    <p className={`leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}>{description}</p>
                    <div className="edge-underline absolute bottom-0 w-0 h-1 bg-[var(--gold-primary)] group-hover:w-full transition-all duration-500" />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AboutSection;
